import type { MutedAlertState } from '@kirin/api-client/contracts/alerts';

import { formatDateTime, formatUtcDate } from '@/lib/date-utils';

/**
 * Build the three text rows shown in the muted-alert tooltip from a
 * {@link MutedAlertState}. Returns `null` when the alert is not muted, so
 * callers can early-return instead of rendering the tooltip.
 *
 * `mutedAt` is a zone-labelled timestamp in `timeZone` ({@link formatDateTime});
 * `mutedUntil` is a UTC calendar date, labelled UTC when `timeZone` is not
 * ({@link formatUtcDate}), both in the user's date format. Components pass
 * `useDisplayTimeZone()` so the tooltip follows a change. `mutedUntil === null`
 * means an indefinite mute and is surfaced as the literal string
 * `"Indefinite"`.
 */
export function formatMutedTooltipLines(
  mute: MutedAlertState | null | undefined,
  timeZone: string
): { muter: string; mutedAt: string; mutedUntil: string } | null {
  if (!mute?.muted) return null;
  const muter = mute.mutedByName ?? mute.mutedByEmail ?? 'Unknown user';
  const mutedAt = formatDateTime(mute.mutedAt, timeZone);
  const mutedUntil = mute.mutedUntil
    ? formatUtcDate(mute.mutedUntil, timeZone !== 'UTC')
    : 'Indefinite';
  return { muter, mutedAt, mutedUntil };
}
